import { useEffect, useMemo, useState } from 'react';
import { get2048Appearance, type Game2048DisplayMode } from '../lib/game2048Appearance';
import { debug } from '../lib/utils';

const loadImage = (src: string): Promise<void> => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve();
  image.onerror = () => reject(new Error('Unable to load 2048 image: ' + src));
  image.src = src;
});

export const use2048Appearance = (
  date: string,
  mode: Game2048DisplayMode,
  changeMode: (next: Game2048DisplayMode) => void,
  isLocked: boolean
) => {
  const appearance = useMemo(() => get2048Appearance(date), [date]);
  const [loadedTheme, setLoadedTheme] = useState<string | null>(null);
  const [failedTheme, setFailedTheme] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const themeId = appearance.themeId;

  useEffect(() => {
    if (mode !== 'pictures') {
      return;
    }
    let cancelled = false;
    setFailedTheme(null);
    Promise.all(Object.values(appearance.images).map(loadImage)).then(() => {
      if (!cancelled) {
        setLoadedTheme(themeId);
      }
    }).catch(error => {
      debug('2048 artwork loading failed:', error);
      if (!cancelled) {
        setFailedTheme(themeId);
      }
    });
    return () => { cancelled = true; };
  }, [appearance, attempt, mode, themeId]);

  const status = failedTheme === themeId ? 'error' : loadedTheme === themeId ? 'ready' : 'loading';
  return {
    mode, changeMode, isLocked, appearance, status,
    isBlocking: mode === 'pictures' && status !== 'ready',
    tileAppearance: mode === 'pictures' && status === 'ready' ? appearance : undefined,
    retry: () => setAttempt(value => value + 1)
  };
};
